'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import * as BasePhoneInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'
import enLabels from 'react-phone-number-input/locale/en.json'

import { Combobox as ComboboxPrimitive } from '@base-ui-components/react/combobox'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/base-button'
import {
  Combobox,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue
} from '@/components/ui/base-combobox'
import { ScrollArea } from '@/components/ui/base-scroll-area'

import { cn, isFieldInvalid } from '@/utils'

import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

type CountryEntry = { label: string; value: BasePhoneInput.Country | undefined }

function FlagComponent({ country, countryName }: BasePhoneInput.FlagProps) {
  const Flag = flags[country]

  return (
    <span className="bg-foreground/10 flex h-4 w-6 overflow-hidden rounded [&_svg:not([class*='size-'])]:size-full!">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

/**
 * Finds a country by its calling code
 * @param callingCode - The calling code (e.g., "966", "+966", or "966")
 * @returns The country code (e.g., "SA") or undefined
 */
const findCountryByCallingCode = (
  callingCode: string | undefined
): BasePhoneInput.Country | undefined => {
  if (!callingCode) return undefined

  // Remove + prefix if present
  const code = callingCode.replace(/^\+/, '')

  // Get all countries
  const countries = BasePhoneInput.getCountries()

  // Find the country that matches this calling code
  for (const country of countries) {
    try {
      const countryCode = BasePhoneInput.getCountryCallingCode(country)
      if (countryCode === code) {
        return country
      }
    } catch {
      // Continue searching
    }
  }

  return undefined
}

export function CountryCodeSelector({
  disabled,
  label,
  placeholder,
  required
}: {
  disabled?: boolean
  label?: string
  placeholder?: string
  required?: boolean
}) {
  const field = useFieldContext()
  const isInvalid = isFieldInvalid(field)
  const t = useTranslations('components.form.phoneInput')
  const tUi = useTranslations('components.ui.phoneInput')
  const [searchValue, setSearchValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  )

  // Get all countries with their labels
  const countryList = useMemo<CountryEntry[]>(() => {
    const countries = BasePhoneInput.getCountries()
    return countries.map((country) => ({
      label: (enLabels[country] as string) || country,
      value: country
    }))
  }, [])

  // Get the current calling code from the form field
  const currentCallingCode = (field.state.value as string | undefined) || ''

  // Find the country that matches the current calling code
  const selectedCountry = useMemo(() => {
    return findCountryByCallingCode(currentCallingCode)
  }, [currentCallingCode])

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!searchValue) return countryList
    return countryList.filter(({ label }) =>
      label.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [countryList, searchValue])

  // Focus the input when it becomes available
  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    const timer = setTimeout(() => {
      input.focus()
    }, 100)

    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement
      if (target && target.value !== searchValue) {
        setSearchValue(target.value)
      }
    }

    input.addEventListener('input', handleInput)

    return () => {
      clearTimeout(timer)
      input.removeEventListener('input', handleInput)
    }
  }, [searchValue])

  // Handle country selection
  const handleCountryChange = (country: BasePhoneInput.Country) => {
    try {
      const callingCode = BasePhoneInput.getCountryCallingCode(country)
      // Store the calling code with + prefix
      field.handleChange(`+${callingCode}`)
    } catch {
      // If getting calling code fails, don't update
    }
  }

  return (
    <FieldWrapper field={field} label={label} required={required}>
      <Combobox
        items={filteredCountries}
        onValueChange={(country) => {
          if (country) {
            handleCountryChange(country as BasePhoneInput.Country)
          }
        }}
        value={selectedCountry || ''}
      >
        <div className="relative">
          <ComboboxTrigger
            render={
              <Button
                className={cn(
                  [
                    'flex h-[42px] w-full gap-1 rounded-xl !px-3.5 !py-2 focus:z-10',
                    'shadow-xs shadow-black/5'
                  ],
                  disabled && 'opacity-50'
                )}
                data-invalid={isInvalid || undefined}
                disabled={disabled}
                mode="input"
                size="md"
                variant="outline"
              >
                <ComboboxValue>
                  {(value: BasePhoneInput.Country) => (
                    <div className="flex items-center gap-2 rtl:flex-row-reverse">
                      <FlagComponent country={value} countryName={value} />
                      <span className="text-sm">
                        {value
                          ? `+${BasePhoneInput.getCountryCallingCode(value)}`
                          : placeholder || t('examplePlaceholder')}
                      </span>
                    </div>
                  )}
                </ComboboxValue>
                <ComboboxIcon />
              </Button>
            }
          />
          <div ref={setPortalContainer} />
          {portalContainer && (
            <ComboboxPrimitive.Portal container={portalContainer}>
              <ComboboxPrimitive.Positioner
                align="start"
                className="pointer-events-auto z-[10000] outline-none"
                side="bottom"
                sideOffset={4}
              >
                <ComboboxPrimitive.Popup
                  className={cn(
                    'bg-popover mt-1 w-[300px] overflow-hidden rounded-md border shadow-md',
                    'data-[state=closed]:hidden'
                  )}
                >
                  <ComboboxInput
                    className={cn([
                      'pointer-events-auto rounded-none border-0 shadow-none',
                      'focus-visible:border-border focus-visible:ring-0 focus-visible:ring-offset-0'
                    ])}
                    onChange={(e) => {
                      const value = e.target.value
                      setSearchValue(value)
                    }}
                    onFocus={(e) => {
                      e.stopPropagation()
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation()
                      }
                    }}
                    onKeyDownCapture={(e) => {
                      e.stopPropagation()
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation()
                    }}
                    placeholder={t('examplePlaceholder')}
                    ref={inputRef}
                    value={searchValue}
                    variant="md"
                  />
                  <ComboboxSeparator />
                  <ComboboxEmpty className="px-2 pt-4 pb-2 text-center">
                    {tUi('noCountryFound')}
                  </ComboboxEmpty>
                  <div
                    className="pointer-events-auto"
                    onTouchMove={(e) => e.stopPropagation()}
                    onWheel={(e) => e.stopPropagation()}
                  >
                    <ScrollArea
                      className={cn(
                        'h-[300px]',
                        '[&_[data-slot=scroll-area-viewport]]:pointer-events-auto',
                        '[&_[data-slot=scroll-area-viewport]]:touch-auto'
                      )}
                    >
                      <ComboboxList className="my-1 overflow-hidden">
                        {filteredCountries.map(({ label, value }) =>
                          value ? (
                            <ComboboxItem
                              className="flex items-center gap-2 ps-3 pe-8 rtl:flex-row-reverse"
                              key={value}
                              value={value}
                            >
                              <FlagComponent
                                country={value}
                                countryName={label}
                              />
                              <span className="flex-1 text-sm">{label}</span>
                              <span className="text-foreground/50 text-sm">
                                {`+${BasePhoneInput.getCountryCallingCode(value)}`}
                              </span>
                              <ComboboxItemIndicator className="start-auto end-2.5" />
                            </ComboboxItem>
                          ) : null
                        )}
                      </ComboboxList>
                    </ScrollArea>
                  </div>
                </ComboboxPrimitive.Popup>
              </ComboboxPrimitive.Positioner>
            </ComboboxPrimitive.Portal>
          )}
        </div>
      </Combobox>
    </FieldWrapper>
  )
}

// Export as CountryCodeSelectorField for form compatibility
export const CountryCodeSelectorField = CountryCodeSelector
